import './listing.scss';

import { IAppliance, IFilterOptions } from '../../interfaces';
import React, { useState } from 'react';

import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import AvailabilityComponent from './AvailabilityComponent';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { LocalizedLink } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import downloadIcon from '@progymedia/sbi-web-englander/src/images/icons/download-icon.svg';
import empty_star from '@progymedia/sbi-web-englander/src/images/icons/Empty_Star_Light.svg';
import full_star from '@progymedia/sbi-web-englander/src/images/icons/Full_Star_Light.svg';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { useTranslation } from 'react-i18next';
import viewIcon from '../../images/englander-icons/View-Icon.svg';

export default function Listing(props: {
    title?: string;
    items: Queries.ProductsJson[];
    hasOwnPageOverride?: boolean;
    displayManual: boolean;
    displayLinkOverride?: boolean;
    displayRatings?: boolean;
    filterOptions?: IFilterOptions;
    displayDescription?: boolean;
    displayPreOrder?: boolean;
}) {
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const isProductDiscontinued = (item: Queries.ProductsJson) => {
        return item?.slug?.['en_CA']?.includes('discontinued');
    };

    const {
        title,
        items,
        hasOwnPageOverride,
        displayManual,
        filterOptions,
        displayRatings,
        displayDescription,
        displayPreOrder,
        displayLinkOverride,
    } = props;

    return (
        <>
            <div className='englander-alist-container'>
                {title && <div className='alist-title'>{title}</div>}
                {items &&
                    items?.map((item, index) => (
                        <Single
                            key={item.code}
                            item={item}
                            index={index}
                            displayLink={displayLinkOverride}
                            displayManual={displayManual}
                            displayRatings={displayRatings}
                            filterOptions={filterOptions}
                            displayDescription={displayDescription}
                            hasOwnPage={hasOwnPageOverride}
                        />
                    ))}
            </div>
        </>
    );

    function Single(props: {
        item: any;
        index: number;
        displayLink?: boolean;
        hasOwnPage?: boolean;
        displayManual?: boolean;
        displayRatings?: boolean;
        filterOptions?: IFilterOptions;
        displayDescription?: boolean;
    }) {
        const [qtyValue, setQtyValue] = useState(1);
        const {
            item,
            index,
            displayRatings,
            displayDescription,
            displayLink,
            hasOwnPage,
        } = props;
        const [price] = useProductPrice(item.code);
        const availability = useAvailabilityInfo(item);
        const productReviews = useProductReviews(item.code);
        const isMobile = useMobileDetect().isMobile();

        const renderStars = () => {
            const averageRating = productReviews?.average ?? 0;
            const stars = [];
            let hasRating = false;

            for (let i = 0; i < 5; i++) {
                if (i < averageRating) {
                    hasRating = true;
                    stars.push(
                        <img
                            width={20}
                            key={`star_${i}`}
                            src={full_star}
                            alt=''
                        />
                    );
                } else {
                    stars.push(
                        <img
                            width={20}
                            key={`star_${i}`}
                            src={empty_star}
                            alt=''
                        />
                    );
                }
            }

            if (hasRating) return stars.map((star) => star);
        };

        const navigateToproductDetails = () => {
            if (isProductDiscontinued(item)) {
                navigateTranslated(`/archives/${item?.code?.toLowerCase()}`);
            } else if (hasOwnPage && item?.slug?.[currentLocale]) {
                navigateTranslated(
                    item?.slug?.[currentLocale]?.replace('/englander', '') + '/'
                );
            }
        };

        return (
            <div
                className={hasOwnPage ? 'alist-single hasLink' : 'alist-single'}
                key={`${index}`}
            >
                <div
                    className='clickable-zone'
                    onClick={navigateToproductDetails}
                >
                    <div className='alist-image'>
                        <GatsbyImageWrapper
                            fields={item.fields}
                            className='list-img'
                        />
                    </div>
                    <div className='alist-info'>
                        <span className='alist-sku'>{item?.code}</span>
                        <span className='alist-info-title'>
                            {item?.title?.[currentLocale]}
                        </span>
                        {item?.description && displayDescription && (
                            <span className='alist-description'>
                                {item?.description?.[currentLocale]}
                            </span>
                        )}
                        {displayManual &&
                            item?.documents?.[0][currentLocale] && (
                                <a
                                    rel='noreferrer'
                                    target='_blank'
                                    href={
                                        '/file/' +
                                        item?.documents[0][currentLocale]
                                    }
                                    className='document'
                                >
                                    <span className='alist-download'>
                                        {t('appliance.download-manual')}
                                        <img src={downloadIcon} alt=''></img>
                                    </span>
                                </a>
                            )}
                        {displayRatings && (
                            <div className='alist-reviews'>
                                {renderStars()}
                                <span className='rating-count'>
                                    {productReviews && productReviews?.count > 0
                                        ? `(${productReviews?.count})`
                                        : ''}
                                </span>
                            </div>
                        )}
                        <div className='availability-link-container'>
                            {isMobile && (
                                <AvailabilityComponent product={item} />
                            )}
                            {displayLink && (
                                <LocalizedLink
                                    className='alist-see-more'
                                    to={item?.slug?.[currentLocale]?.replace(
                                        '/englander',
                                        ''
                                    )}
                                >
                                    {t('header.view-product')}
                                    <img
                                        className={`alist-iconRight`}
                                        src={viewIcon}
                                        alt={''}
                                    />
                                </LocalizedLink>
                            )}
                        </div>
                    </div>

                    <div className={`mobile-cart-group`}>
                        <div className='mobile-price-group'>
                            {!isMobile && (
                                <AvailabilityComponent product={item} />
                            )}
                            <div className='alist-price'>
                                {LocalePrice(
                                    currentLocale,
                                    price?.regularPrice.value
                                )}
                            </div>
                        </div>
                        <div className='alist-add-to-cart'>
                            {displayPreOrder &&
                                availability.availabilityLabel?.includes(
                                    t('addToCartBtn.next-availability')
                                ) &&
                                availability.allowAddToCart && (
                                    <span className='preorder-txt'>
                                        {t('addToCartBtn.pre-order')}
                                    </span>
                                )}
                            <AddToCartButtonContainer
                                product={item}
                                qtyValue={qtyValue}
                                setQtyValue={setQtyValue}
                                small={isMobile}
                                horizontalLayout={isMobile}
                                showCartIcon={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
